import { AxiosError } from 'axios';
import {
  AGREEMENT_ERROR_STATUSES,
  AGREEMENT_FIELD_GROUP_BY_TYPE,
  AGREEMENT_FIELD_GROUPS,
  AGREEMENT_FIELD_TYPES,
  AGREEMENT_FIELDS_INVALID_REASONS,
  AGREEMENT_LIMITS,
  AGREEMENT_SIGNING_CONTEXT_TYPES,
  AgreementField,
  AgreementFieldType,
  AgreementSigningSession,
  AgreementTemplate,
  NDA_PROVIDERS,
} from 'models/Agreement';
import { ONE_MB } from 'data/global';
import { getAxiosErrorStatusCode } from 'axiosUtils';
import { LeadNdaProps } from 'models/Lead';
import { Listing } from 'models/Listing';
import { TFunction } from 'i18next';
import { DataRoomNda } from 'router/subrouters/DataRoom/models/DataRoomNDA';

export const getAgreementInvalidFieldIds = (error: AxiosError): string[] =>
  error?.response?.data?.fieldIds || [];

export const getHasSignatureField = (fields: AgreementField[]) =>
  fields.some(({ type }) => type === AGREEMENT_FIELD_TYPES.SIGNATURE);

export const getIsDateSignedField = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.DATE_SIGNED;

export const getIsAgreementFileTooLarge = (file: File) =>
  file.size > AGREEMENT_LIMITS.MAX_FILE_SIZE_MB * ONE_MB;

export const getIsSignatureField = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.SIGNATURE;

export const getAgreementErrorCode = (error: AxiosError): string =>
  error?.response?.data?.code || '';

export const getIsTooManyRequests = (error: AxiosError) =>
  getAxiosErrorStatusCode(error) === AGREEMENT_ERROR_STATUSES.TOO_MANY_REQUESTS;

export const getIsNativeNda = (props?: Pick<LeadNdaProps, 'ndaProvider'>) =>
  props?.ndaProvider === NDA_PROVIDERS.NATIVE;

export const getIsListingNativeEsignActive = (
  listing?: Pick<Listing, 'isNativeEsignEnabled' | 'agreementTemplateId'>,
) => !!listing?.isNativeEsignEnabled && !!listing?.agreementTemplateId;

export const getHasNdaToSend = (
  envelopeTemplateId: string,
  listing?: Pick<Listing, 'isNativeEsignEnabled' | 'agreementTemplateId'>,
) => !!envelopeTemplateId || getIsListingNativeEsignActive(listing);

export const getIsNotFoundError = (error?: AxiosError | null) =>
  !!error && getAxiosErrorStatusCode(error) === AGREEMENT_ERROR_STATUSES.NOT_FOUND;

export const getAgreementAttemptsLeft = (error: AxiosError): number =>
  error?.response?.data?.attemptsLeft;

export const AGREEMENT_FIELD_LABEL_KEYS: Record<AgreementFieldType, string> = {
  [AGREEMENT_FIELD_TYPES.SIGNATURE]: 'agreementFieldSignature',
  [AGREEMENT_FIELD_TYPES.FULL_NAME]: 'fullName',
  [AGREEMENT_FIELD_TYPES.EMAIL]: 'email',
  [AGREEMENT_FIELD_TYPES.DATE_SIGNED]: 'agreementFieldDateSigned',
  [AGREEMENT_FIELD_TYPES.TIME_SIGNED]: 'agreementFieldTimeSigned',
  [AGREEMENT_FIELD_TYPES.PHONE]: 'mobile',
  [AGREEMENT_FIELD_TYPES.COMPANY]: 'agreementFieldCompany',
  [AGREEMENT_FIELD_TYPES.ABN]: 'agreementFieldAbn',
  [AGREEMENT_FIELD_TYPES.FUNDING_SOURCE]: 'agreementFieldFundingSource',
  [AGREEMENT_FIELD_TYPES.PURCHASE_BUDGET]: 'agreementFieldPurchaseBudget',
  [AGREEMENT_FIELD_TYPES.CUSTOM_TEXT]: 'agreementFieldCustomQuestion',
  [AGREEMENT_FIELD_TYPES.CHECKBOX]: 'agreementFieldCheckbox',
};

export const getAgreementFieldLabel = (field: AgreementField, t: TFunction) =>
  field.label?.trim() || t(AGREEMENT_FIELD_LABEL_KEYS[field.type]);

export const getIsSignatureRequiredError = (error: AxiosError) =>
  error?.response?.data?.reason ===
  AGREEMENT_FIELDS_INVALID_REASONS.SIGNATURE_REQUIRED;

export const getIsAgreementTemplateActive = (template?: AgreementTemplate) =>
  !!template?.isActive && !!template.currentVersion;

export const AGREEMENT_SIGNING_RETURN_PARAM = 'returnTo';

export const getSigningPathWithReturnPath = (url: string, returnPath: string) => {
  const { pathname, searchParams } = new URL(url, window.location.origin);

  searchParams.set(AGREEMENT_SIGNING_RETURN_PARAM, returnPath);

  return `${pathname}?${searchParams.toString()}`;
};

export const getIsNativeDataRoomNda = (
  nda?: Pick<DataRoomNda, 'provider'> | null,
) => nda?.provider === NDA_PROVIDERS.NATIVE;

export const getSecureLinkSigningContext = (
  session?: Pick<AgreementSigningSession, 'context'>,
) =>
  session?.context?.type === AGREEMENT_SIGNING_CONTEXT_TYPES.SECURE_LINK
    ? session.context
    : undefined;

export const getAgreementFieldGroup = ({
  type,
}: Pick<AgreementField, 'type'>) => AGREEMENT_FIELD_GROUP_BY_TYPE[type];

export const getIsAutoFilledField = (field: Pick<AgreementField, 'type'>) =>
  getAgreementFieldGroup(field) === AGREEMENT_FIELD_GROUPS.AUTO;

export const getIsBuyerField = (field: Pick<AgreementField, 'type'>) =>
  getAgreementFieldGroup(field) === AGREEMENT_FIELD_GROUPS.BUYER;

export const getIsCheckboxField = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.CHECKBOX;

export const getHasCustomLabel = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.CUSTOM_TEXT ||
  type === AGREEMENT_FIELD_TYPES.CHECKBOX;

export const getHasEmptyCustomLabel = (fields: AgreementField[]) =>
  fields.some((field) => getHasCustomLabel(field) && !field.label?.trim());

export const getIsSigningTimeField = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.DATE_SIGNED ||
  type === AGREEMENT_FIELD_TYPES.TIME_SIGNED;

export const getShowsRequiredMark = (field: AgreementField) =>
  field.required && !getIsAutoFilledField(field);

export const getAgreementFieldMaxLength = (field: AgreementField) =>
  field.maxLength || AGREEMENT_LIMITS.FIELD_VALUE_MAX_LENGTH;

export const getAgreementFieldText = (field: AgreementField, t: TFunction) =>
  `${getAgreementFieldLabel(field, t)}${
    getShowsRequiredMark(field) ? ' *' : ''
  }`;

export const getIsPhoneField = ({ type }: Pick<AgreementField, 'type'>) =>
  type === AGREEMENT_FIELD_TYPES.PHONE;

export const getIsLabelInvalidError = (error: AxiosError) =>
  error?.response?.data?.reason ===
  AGREEMENT_FIELDS_INVALID_REASONS.LABEL_INVALID;

export const getIsCustomQuestionField = ({
  type,
}: Pick<AgreementField, 'type'>) => type === AGREEMENT_FIELD_TYPES.CUSTOM_TEXT;
